import { findSceneNode, type CurvaDocument } from "@/document/model/document";
import { opaqueFrameFillClearColor } from "@/export/frameExportBackground";
import { documentForPngExport } from "@/export/pngExporter";
import type { Rect } from "@/lib/math/rect";
import type { Renderer } from "@/renderer/Renderer";
import { prepareWasmMeshKernels } from "@/renderer/scene/tessellation/curvaTessWasm";

export interface ScriptPreviewRenderOptions {
  document: CurvaDocument;
  frameId: string;
  /** Backing-store scale vs frame CSS pixels (typically devicePixelRatio). */
  scale: number;
}

export interface ScriptPreviewRenderResult {
  frameId: string;
  bounds: Rect;
}

/** Render a scripted frame from a (usually working-copy) document into a canvas. */
export async function renderScriptPreviewToCanvas(
  renderer: Renderer,
  options: ScriptPreviewRenderOptions,
): Promise<ScriptPreviewRenderResult | undefined> {
  await prepareWasmMeshKernels();
  const frame = findSceneNode(options.document, options.frameId);
  if (frame?.type !== "frame") {
    return undefined;
  }

  const exportDocument = documentForPngExport(options.document, frame);
  await renderer.renderDocumentToCanvas(exportDocument, frame.bounds, Math.max(1, options.scale), {
    clearColor: opaqueFrameFillClearColor(frame),
  });

  return {
    frameId: frame.id,
    bounds: { ...frame.bounds },
  };
}

/** Backing-store scale so 1 frame unit covers `displayWidth` CSS pixels × DPR. */
export function scriptPreviewRenderScale(frameWidth: number, displayWidthCss: number): number {
  const dpr = typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1;
  const width = Math.max(1, frameWidth);
  const display = Math.max(1, displayWidthCss);
  return Math.max(1, (display / width) * dpr);
}
